/**
 * Module #34 Phase A chunk 7 — shared insight-brief renderer.
 *
 * The "What needs attention" card list, grouped by category, with one-click
 * direct-fire (chunk 7 ⑦) + dismiss. Used by BOTH the floating CopilotPanel
 * (collapsible sections, compact) and the dedicated /admin/brief page (chunk 7
 * ⑥; all sections expanded). Owns the inline confirm/fire state so the two
 * surfaces stay identical and DRY.
 *
 * Tier-1/2 actions fire directly through onFireAction after an inline confirm.
 * Tier-3 actions need a server-supplied challenge phrase, so they route through
 * the chat (onSeedChat) — provided by the panel, omitted on the page (where a
 * Tier-3 button is disabled with a hint to use the copilot chat).
 */

import { type CSSProperties, type ReactElement, useState } from 'react';
import type {
  CopilotInsight,
  CopilotSuggestedAction,
  FireActionResult,
  InsightCategory,
} from '../../hooks/useCopilotInsights';

/** Section order for the brief (money first, then customer-facing, then upside). */
const CATEGORY_ORDER: InsightCategory[] = [
  'accounts_receivable',
  'customer_messages',
  'tasks',
  'opportunities',
];

const CATEGORY_LABEL: Record<InsightCategory, string> = {
  accounts_receivable: 'Accounts Receivable',
  customer_messages: 'Customer Messages',
  tasks: 'Tasks',
  opportunities: 'Opportunities',
};

/**
 * Build a chat seed for a Tier-3 card action. Sending it makes the copilot
 * propose the exact tool call (referencing the opaque pseudonym, so PII stays
 * out of the LLM); the chat typed-confirm modal then gates the send with the
 * server-supplied challenge phrase. Tier-1/2 actions don't use this — they fire
 * directly after the inline confirm.
 */
function seedPromptFor(a: CopilotSuggestedAction): string {
  if (a.toolName === 'send_pay_now_email' && typeof a.input.invoice_pseudonym_id === 'string') {
    return `Send the pay-now email for invoice ${a.input.invoice_pseudonym_id}.`;
  }
  const args = Object.entries(a.input)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(', ');
  return `${a.label} (${args}).`;
}

function priorityColor(priority: CopilotInsight['priority']): string {
  switch (priority) {
    case 'critical':
      return 'var(--danger, #dc2626)';
    case 'high':
      return 'var(--warning, #d97706)';
    case 'normal':
      return 'var(--system-brand-accent, #0a0045)';
    default:
      return 'var(--text-muted, #6b7280)';
  }
}

export interface InsightBriefProps {
  insights: CopilotInsight[];
  /** Dismiss one card (optimistic). */
  onDismiss: (id: number) => void;
  /** Fire a card's pre-staged action directly (no chat round-trip). */
  onFireAction: (
    id: number,
    actionIndex: number,
    confirmationText?: string,
  ) => Promise<FireActionResult>;
  /** Tier-3 fallback: seed the copilot chat. Omit on surfaces without a chat. */
  onSeedChat?: (prompt: string) => void;
  /** true → collapsible category sections (panel); false → all expanded (page). */
  collapsible?: boolean;
  /** Disable action buttons while an external turn is in flight (the panel chat). */
  busy?: boolean;
}

export function InsightBrief({
  insights,
  onDismiss,
  onFireAction,
  onSeedChat,
  collapsible = false,
  busy = false,
}: InsightBriefProps): ReactElement {
  // Collapsible sections (panel) start collapsed; the page renders all expanded.
  const [openCategories, setOpenCategories] = useState<Set<InsightCategory>>(() => new Set());
  const toggleCategory = (cat: InsightCategory): void =>
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  // Direct-fire state. `confirming` holds the single card+action awaiting an
  // inline Confirm; `firing` blocks double-clicks; `fired` records the per-card
  // outcome so the card shows "✓ Sent" instead of re-offering the button.
  const [confirming, setConfirming] = useState<{ insightId: number; actionIndex: number } | null>(
    null,
  );
  const [firing, setFiring] = useState(false);
  const [fired, setFired] = useState<Record<number, FireActionResult>>({});

  async function doFire(insightId: number, actionIndex: number): Promise<void> {
    setFiring(true);
    try {
      const result = await onFireAction(insightId, actionIndex);
      setFired((prev) => ({ ...prev, [insightId]: result }));
      setConfirming(null);
    } finally {
      setFiring(false);
    }
  }

  function renderActionArea(i: CopilotInsight): ReactElement {
    const firedResult = fired[i.insightId];
    const confirmActionIndex =
      confirming !== null && confirming.insightId === i.insightId ? confirming.actionIndex : null;

    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginTop: '0.5rem',
          alignItems: 'center',
        }}
      >
        {firedResult !== undefined ? (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: firedResult.ok ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)',
            }}
          >
            {firedResult.ok ? '✓ ' : '✗ '}
            {firedResult.message}
          </span>
        ) : confirmActionIndex !== null ? (
          <>
            <span style={{ fontSize: '0.75rem', color: 'var(--text)' }}>
              {i.suggestedActionCalls[confirmActionIndex]?.label ??
                i.suggestedActionCalls[confirmActionIndex]?.toolName ??
                'this action'}
              ?
            </span>
            <button
              type="button"
              disabled={firing}
              onClick={() => void doFire(i.insightId, confirmActionIndex)}
              style={primaryBtn(firing)}
            >
              {firing ? 'Working…' : 'Confirm'}
            </button>
            <button
              type="button"
              disabled={firing}
              onClick={() => setConfirming(null)}
              style={mutedBtn}
            >
              Cancel
            </button>
          </>
        ) : (
          i.suggestedActionCalls.map((a, idx) => {
            const isTier3 = a.tier === 'tier3';
            // Tier-3 needs a chat round-trip for the typed phrase. Without a chat
            // surface (the page), disable it with a hint.
            const tier3NoChat = isTier3 && onSeedChat === undefined;
            return (
              <button
                key={idx}
                type="button"
                disabled={busy || firing || tier3NoChat}
                onClick={() => {
                  if (isTier3) onSeedChat?.(seedPromptFor(a));
                  else setConfirming({ insightId: i.insightId, actionIndex: idx });
                }}
                title={
                  tier3NoChat
                    ? 'Open this card in the copilot chat to confirm'
                    : isTier3
                      ? 'Opens a typed confirmation in the copilot chat'
                      : 'Fires this action after a quick confirm — no chat round-trip'
                }
                style={primaryBtn(busy || firing || tier3NoChat)}
              >
                {a.label ?? a.toolName}
              </button>
            );
          })
        )}
        {confirmActionIndex === null && (
          <button type="button" onClick={() => onDismiss(i.insightId)} style={mutedBtn}>
            Dismiss
          </button>
        )}
      </div>
    );
  }

  function renderCard(i: CopilotInsight): ReactElement {
    return (
      <div
        key={i.insightId}
        style={{
          border: '1px solid var(--border)',
          borderRadius: '0.5rem',
          padding: '0.625rem',
          borderLeft: `3px solid ${priorityColor(i.priority)}`,
          background: 'var(--surface)',
        }}
      >
        {/* pre-wrap preserves the line breaks/bullets in multi-section narratives
            (the weekly strategic brief); single-line operational cards are
            unaffected. */}
        <p
          style={{
            margin: 0,
            fontSize: '0.8125rem',
            lineHeight: 1.5,
            color: 'var(--text)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {i.narrative}
        </p>
        {renderActionArea(i)}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      {CATEGORY_ORDER.map((cat) => {
        const items = insights.filter((i) => i.category === cat);
        if (items.length === 0) return null;
        const isOpen = !collapsible || openCategories.has(cat);
        const sectionId = `insight-section-${cat}`;
        return (
          <div key={cat} style={{ display: 'grid', gap: '0.5rem' }}>
            {collapsible ? (
              <button
                type="button"
                onClick={() => toggleCategory(cat)}
                aria-expanded={isOpen}
                aria-controls={sectionId}
                style={sectionHeaderBtn}
              >
                <span aria-hidden="true" style={{ width: '0.75rem', fontSize: '0.7rem' }}>
                  {isOpen ? '▾' : '▸'}
                </span>
                <span>
                  {CATEGORY_LABEL[cat]} ({items.length})
                </span>
              </button>
            ) : (
              <div style={sectionHeaderText}>
                {CATEGORY_LABEL[cat]} ({items.length})
              </div>
            )}
            {isOpen && (
              <div id={sectionId} style={{ display: 'grid', gap: '0.5rem' }}>
                {items.map((i) => renderCard(i))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function primaryBtn(disabled: boolean): CSSProperties {
  return {
    border: '1px solid var(--border)',
    background: 'var(--system-brand-accent)',
    color: 'var(--system-brand-accent-fg)',
    borderRadius: '0.375rem',
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.55 : 1,
  };
}

const mutedBtn: CSSProperties = {
  border: '1px solid var(--border)',
  background: 'var(--surface-elev)',
  color: 'var(--text-muted)',
  borderRadius: '0.375rem',
  padding: '0.25rem 0.5rem',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const sectionHeaderBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '0.25rem 0',
  fontSize: '0.6875rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
};

const sectionHeaderText: CSSProperties = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  padding: '0.25rem 0',
};
